import {
  Button,
  Col,
  DatePicker,
  Layout,
  Row,
  Tabs,
  Table,
  Dropdown,
  Menu,
} from "antd";
import { Eye, Pen, ChevronDown } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import {
  formatDate,
  formatNumber,
  getDateRange,
  handleChangePage,
  handlePageSizeChange,
  navigateTo,
} from "../../Utils/Common";
import { getRequest } from "../../Utils/ApiCalls";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const PettyCashEntry = () => {
  const [loading, setLoading] = useState("");
  const dateRange = getDateRange("week");
  const [filterConfig, setFilterConfig]: any = useState({
    date_from: formatDate(dateRange[0], "YYYY-MM-DD"),
    date_to:   formatDate(dateRange[1], "YYYY-MM-DD"),
    page:      1,
    status:    "pending",
  });
  const [dataSize, setDataSize] = useState(0);
  const [dataSource, setDataSource]: any = useState([]);
  const [activeTab, setActiveTab] = useState("pending");

  function onChangeTab(activeKey: any) {
    setActiveTab(activeKey);
    setFilterConfig((prev: any) => ({
      ...prev,
      status: activeKey,
      page:   1,
    }));
  }

  function getTableColumns() {
    const baseColumns = [
      {
        title:     "Transaction Date",
        dataIndex: "transaction_date",
        key:       "transaction_date",
        render:    (date: any) => formatDate(date, "MMM DD, YYYY"),
      },
      {
        title:     "Account",
        dataIndex: "account",
        key:       "account",
      },
      {
        title:     "Department",
        dataIndex: "department",
        key:       "department",
      },
      {
        title:     "Branch",
        dataIndex: "branch_name",
        key:       "branch_name",
      },
      {
        title:     "Requested By",
        dataIndex: "requested_by",
        key:       "requested_by",
      },
      {
        title:     "Amount",
        dataIndex: "amount",
        key:       "amount",
        align:     "right" as const,
        render:    (amount: any) => formatNumber(amount),
      },
      {
        title:  "Remarks",
        dataIndex: "remarks",
        key:    "remarks",
        render: (remarks: any) => remarks ?? "-",
      },
      {
        title:     "Added By",
        dataIndex: "added_by_name",
        key:       "added_by_name",
      },
    ];

    switch (activeTab) {
      case "pending":
        return [
          ...baseColumns,
          {
            title:  "",
            key:    "action",
            width:  10,
            align:  "center" as const,
            render: (_text: any, record: any) => (
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item
                      key="view"
                      icon={<Eye strokeWidth={1.5} size={15} />}
                      onClick={() => navigateTo(`/petty-cash-entry/view/${record.id}`)}
                    >
                      Review
                    </Menu.Item>
                    <Menu.Item
                      key="edit"
                      icon={<Pen strokeWidth={1.5} size={15} />}
                      onClick={() => navigateTo(`/petty-cash-entry/edit/${record.id}`)}
                    >
                      Edit
                    </Menu.Item>
                  </Menu>
                }
                trigger={["click"]}
              >
                <Button>Actions <ChevronDown strokeWidth={0.5} /></Button>
              </Dropdown>
            ),
          },
        ];

      case "approved":
        return [
          ...baseColumns,
          {
            title:     "Approved By",
            dataIndex: "approver_name",
            key:       "approver_name",
          },
          {
            title:  "Action",
            key:    "action",
            align:  "center" as const,
            render: (_text: any, record: any) => (
              <Button
                type="primary"
                size="small"
                onClick={() => navigateTo(`/petty-cash-entry/view/${record.id}`)}
              >
                View
              </Button>
            ),
          },
        ];

      case "disapproved":
        return [
          ...baseColumns,
          {
            title:     "Disapproved By",
            dataIndex: "disapprover_name",
            key:       "disapprover_name",
          },
          {
            title:     "Reason",
            dataIndex: "reason",
            key:       "reason",
          },
          {
            title:  "Action",
            key:    "action",
            align:  "center" as const,
            render: (_text: any, record: any) => (
              <Button
                type="primary"
                size="small"
                onClick={() => navigateTo(`/petty-cash-entry/view/${record.id}`)}
              >
                View
              </Button>
            ),
          },
        ];

      default:
        return baseColumns;
    }
  }

  function handleFilterChange(value: any, field: string) {
    if (field === "dates") {
      if (!value || value.length !== 2) {
        setFilterConfig((prev: any) => ({
          ...prev,
          date_from: null,
          date_to:   null,
        }));
        return;
      }
      setFilterConfig((prev: any) => ({
        ...prev,
        date_from: value[0]?.format("YYYY-MM-DD"),
        date_to:   value[1]?.format("YYYY-MM-DD"),
      }));
    } else {
      setFilterConfig((prev: any) => ({
        ...prev,
        [field]: value,
      }));
    }
  }

  async function fetchTableData() {
    setDataSource([]);
    setLoading("fetch");
    const response = await getRequest(`petty_cash_entries/get`, filterConfig, true);
    if (response.success) {
      setDataSize(response.data.size);
      setDataSource(response.data.data);
    }
    setLoading("");
  }

  useEffect(() => {
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Petty Cash Entry"
              withDateRangeFilter={false}
              withAddButton={true}
              addBtnText="Add Entry"
              onCreate={() => navigateTo("/petty-cash-entry/add")}
              withSearchBtn={true}
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({ ...prev, search: value }))
              }
            />
          </Col>
          <Col xs={24} className="mb-1">
            <span>
              <strong>Filter By:</strong>
            </span>
          </Col>
          <Col xs={24} md={12} lg={8} className="mb-3">
            <RangePicker
              value={[
                filterConfig.date_from ? dayjs(filterConfig.date_from) : null,
                filterConfig.date_to   ? dayjs(filterConfig.date_to)   : null,
              ]}
              format="MMMM D, YYYY"
              onChange={(e) => handleFilterChange(e, "dates")}
              allowClear
              className="w-full"
            />
          </Col>
          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="min-w-[800px]">
                <Tabs
                  type="card"
                  className="custom-tabs"
                  activeKey={activeTab}
                  onTabClick={onChangeTab}
                >
                  <TabPane tab="Cash In Request" key="pending">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={loading === "fetch"}
                      size="small"
                      pagination={{
                        current:          filterConfig.page,
                        total:            dataSize,
                        showSizeChanger:  true,
                        onChange:         (page: number) =>
                          handleChangePage(page, setFilterConfig),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size, setFilterConfig),
                      }}
                    />
                  </TabPane>
                  <TabPane tab="Approved" key="approved">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={loading === "fetch"}
                      size="small"
                      pagination={{
                        current:          filterConfig.page,
                        total:            dataSize,
                        showSizeChanger:  true,
                        onChange:         (page: number) =>
                          handleChangePage(page, setFilterConfig),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size, setFilterConfig),
                      }}
                    />
                  </TabPane>
                  <TabPane tab="Disapproved" key="disapproved">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={loading === "fetch"}
                      size="small"
                      pagination={{
                        current:          filterConfig.page,
                        total:            dataSize,
                        showSizeChanger:  true,
                        onChange:         (page: number) =>
                          handleChangePage(page, setFilterConfig),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size, setFilterConfig),
                      }}
                    />
                  </TabPane>
                </Tabs>
              </div>
            </div>
          </Col>
        </Row>
      </Layout>
      <PersistentFooter />
    </Layout>
  );
};

export default PettyCashEntry;